"t";
import React, { useState, useRef } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  useMediaQuery,
  IconButton,
  styled
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import {
  Technology,
  Missions,
  AboutIED,
  Leadership,
  Differentiators
} from "../constant";

const borderStyles = {
  border: "0.92px solid",
  borderImageSource: "radial-gradient(80.38% 222.5% at -13.75% -12.36%, #98F9FF 0%, rgba(255, 255, 255, 0) 100%), radial-gradient(80.69% 208.78% at 108.28% 112.58%, #EABFFF 0%, rgba(135, 38, 183, 0) 100%)",
};

const GlassBox = styled(Box)(({ theme }) => ({
  background: "radial-gradient(90.16% 143.01% at 15.32% 21.04%, rgba(165, 239, 255, 0.2) 0%, rgba(110, 191, 244, 0.05) 77.08%, rgba(70, 144, 213, 0.1) 100%)",
  ...borderStyles,
  borderRadius: theme.spacing(2),
  backdropFilter: "blur(10px)",
  position: "relative",
  overflow: "hidden",
  height: "100%",
  width: '100%',
  margin: ' 0 auto',
  [theme.breakpoints.down("md")]: {
    width: '90%'
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  "& .MuiTabs-flexContainer": {
    [theme.breakpoints.down("md")]: {
      justifyContent: "flex-start",
      overflowX: "auto",
      "&::-webkit-scrollbar": {
        display: "none"
      },
    },
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "#61D1FA",
    height: 3,
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  color: "#ffffff50",
  fontWeight: 400,
  fontSize: "1rem",
  textTransform: "none",
  minWidth: "unset",
  padding: theme.spacing(1, 2),
  "&.Mui-selected": {
    color: "#FFFFFF",
    fontWeight: 600,
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.875rem",
  },
}));

const VideoControls = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1),
  background: "linear-gradient(transparent, rgba(0, 0, 0, 0.5))",
  color: "#FFFFFF",
  transition: "opacity 0.3s",
}));

const ProgressBar = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 48,
  left: 0,
  right: 0,
  height: 4,
  backgroundColor: "rgba(255, 255, 255, 0.3)",
  cursor: "pointer",
}));

const ProgressFill = styled(Box)(({ width }) => ({
  height: "100%",
  width: `${width}%`,
  backgroundColor: "#98F9FF",
}));



const HeroAboutVideo = ({ isNetzero, isProduct, url = null }) => {
  const [activeTab, setActiveTab] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const progressBarRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const tabsData = [
    {
      label: "About",
      key: "1",
      videoUrl: isProduct ? url : AboutIED
    },
    {
      label: "Mission",
      key: "2",
      videoUrl: Missions
    },
    {
      label: "Leadership",
      key: "3",
      videoUrl: Leadership
    },
    {
      label: "Differentiators",
      key: "4",
      videoUrl: Differentiators
    },
    {
      label: "Technology",
      key: "5",
      videoUrl: Technology,
    },
  ];

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
      setProgress(0);
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        videoRef.current.requestFullscreen();
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const currentProgress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(currentProgress);
    }
  };

  const handleProgressBarClick = (e) => {
    if (progressBarRef.current && videoRef.current) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      videoRef.current.currentTime = pos * videoRef.current.duration;
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const currentVideo = tabsData[activeTab]?.videoUrl || '';
  const currentTime = videoRef.current ? formatTime(videoRef.current.currentTime) : '0:00';
  const duration = videoRef.current ? formatTime(videoRef.current.duration) : '0:00';


  const isVideoFile = (url) => {
    if (!url) return false;

    return /\.(mp4|webm|ogg|mov)$/i.test(url);
  };

  return (
    <Box sx={{ width: '100%', backgroundColor: isProduct ? "transparent" : "#000", marginTop: 0 }}>
      <Box sx={{
        width: "100%",
        minHeight: { xs: "400px", sm: "620px", md: "720px" },
        p: { sm: 2, md: 3 },
        boxSizing: "border-box",
        maxWidth: "1250px",
        margin: '0 auto',
        backgroundColor: isProduct ? "transparent" : "#000"
      }}>
        <GlassBox>
          {isNetzero && (
            <Box sx={{ p: { xs: 1, sm: 2 } }}>
              <StyledTabs
                value={activeTab}
                onChange={handleChange}
                variant={isMedium ? "scrollable" : "standard"}
                scrollButtons={isMedium ? "auto" : false}
                allowScrollButtonsMobile
                centered={!isMedium || (isNetzero && isProduct)}
                sx={{
                  "& .MuiTabs-flexContainer": {
                    [theme.breakpoints.down("md")]: {
                      justifyContent: "flex-start",
                      overflowX: "auto",
                      "&::-webkit-scrollbar": {
                        display: "none"
                      },
                    },
                    [theme.breakpoints.up("md")]: {
                      justifyContent: (isNetzero && isProduct) ? "center" : "flex-start",
                    },
                  },
                }}
              >
                {tabsData?.map((tab) => (
                  <StyledTab
                    key={tab.key}
                    label={tab.label}
                    id={`tab-${tab.key}`}
                    aria-controls={`tabpanel-${tab.key}`}
                  />
                ))}
              </StyledTabs>
            </Box>
          )}

          <Box
            sx={{
              position: "relative",
              width: isMobile ? "95%" : "95%",
              height: { xs: "250px", sm: "350px", md: "550px" },
              backgroundColor: "#000",
              overflow: "hidden",
              margin: isNetzero ? "0px auto 14px" : "14px auto 14px",
              borderRadius: "12px",
            }}
            onMouseMove={handleMouseMove}
            onMouseLeave={() =>
              isVideoFile(currentVideo) &&
              isPlaying &&
              setShowControls(false)
            }
          >
            {isVideoFile(currentVideo) ? (
              <Box
                component="video"
                ref={videoRef}
                src={currentVideo}
                autoPlay
                loop
                muted={isMuted}
                playsInline
                preload="metadata"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "fill",
                }}
                onTimeUpdate={handleTimeUpdate}
                //onEnded={() => setIsPlaying(false)}
              />
            ) : (
              <Box
                component="img"
                src={currentVideo}
                alt="Media"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "fill",
                  display: "block",
                }}
              />
            )}

            {isVideoFile(currentVideo) && (
              <>
                {/* <ProgressBar
                  ref={progressBarRef}
                  onClick={handleProgressBarClick}
                  sx={{ opacity: showControls ? 0 : 0 }}
                >
                  <ProgressFill width={progress} />
                </ProgressBar> */}

                <VideoControls
                  sx={{
                    opacity: showControls ? 1 : 0,
                  }}
                >
                  {/* <IconButton
                    color="inherit"
                    onClick={togglePlay}
                    size={isMobile ? "small" : "medium"}
                  >
                    {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                  </IconButton> */}

                  <IconButton
                    color="inherit"
                    onClick={toggleMute}
                    size={isMobile ? "small" : "medium"}
                  >
                    {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                  </IconButton>

                  {/* <Typography
                    variant={isMobile ? "caption" : "body2"}
                    sx={{ mx: 1 }}
                  >
                    {currentTime} / {duration}
                  </Typography> */}

                  <Box sx={{ flexGrow: 1 }} />

                  <IconButton
                    color="inherit"
                    onClick={toggleFullscreen}
                    size={isMobile ? "small" : "medium"}
                  >
                    <FullscreenIcon />
                  </IconButton>
                </VideoControls>
              </>
            )}
          </Box>
        </GlassBox>
      </Box>
    </Box>

  );
};

export default HeroAboutVideo;